import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog } from "@mui/material"
import type { FC } from "react"
import React from "react"
import { FormProvider, useForm } from "react-hook-form"

import type { Role, RoleUpdateInput } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { RolesDialogContent } from "@/screens/SystemAdmin/components/dialogs"
import { RoleForm } from "@/screens/SystemAdmin/components/forms"
import { useRole } from "@/screens/SystemAdmin/hooks"
import { RoleFormSchema } from "@/screens/SystemAdmin/schema"
import { SystemAdminDialog } from "@/screens/SystemAdmin/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface EditRoleDialogProps {
  isOpen: SystemAdminDialog | null
  role: Role
  onClose: () => void
  onSuccess: () => void
}

const EditRoleDialogProps: FC<EditRoleDialogProps> = ({ isOpen, onClose, role, onSuccess }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { updateRole } = useRole()

  const methods = useForm<RoleUpdateInput>({
    defaultValues: {
      name: role.name,
      permissionIds: role?.permissions?.map((permission) => permission?.permissionId) ?? [],
    },
    mode: "all",
    resolver: zodResolver(RoleFormSchema),
  })

  const {
    formState: { isValid, isSubmitting, isDirty },
    handleSubmit,
  } = methods

  const isSubmitDisabled = !(isValid && !isSubmitting && isDirty)

  const handleSubmitData = async (fields: RoleUpdateInput) => {
    const { error, data } = await updateRole({
      input: { ...fields, roleId: role.roleId },
    })
    const hasError =
      error?.message || (data?.role.update.__typename === "RoleFailure" && Boolean(data?.role.update.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to update role.",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Role successfully updated.",
    })
    onSuccess()
  }

  const handleCancel = () => {
    onClose()
  }

  return (
    <Dialog open={isOpen === SystemAdminDialog.EDIT_ROLE} onClose={handleCancel} fullWidth>
      <RolesDialogContent
        isSubmitDisabled={isSubmitDisabled}
        title='Edit Role'
        handleCancel={handleCancel}
        handleSave={handleSubmit(handleSubmitData)}
      >
        <FormProvider {...methods}>
          <RoleForm isEditMode={true} />
        </FormProvider>
      </RolesDialogContent>
    </Dialog>
  )
}

export default EditRoleDialogProps
